import { useMemo } from 'react';
import { BRAND } from '../branding';
import { usePharmacyStore, type Pharmacy } from '../stores/pharmacyStore';

export interface ResolvedBranding {
  /** Instance display name — stays the build-time brand (SMS copy, sheet text depend on it). */
  name: string;
  /** Full-size logo for the sidebar, kiosk header and printed sheets. */
  logo: string;
  /** Compact logo for the topbar. */
  logoSmall: string;
  /** Where the logo came from — the pharmacy's uploaded logo, or the build-time brand. */
  source: 'pharmacy' | 'build';
}

/**
 * PRX-26: the pharmacy's uploaded logo (System Configuration → Pharmacy
 * branding) wins over the build-time brand; without one, the build-time
 * brand is used exactly as before. Pure, so it is easy to test and to reuse
 * outside React (print HTML builders).
 */
export function resolveBranding(pharmacy: Pick<Pharmacy, 'branding'> | null | undefined): ResolvedBranding {
  const logo = pharmacy?.branding?.logoDataUrl;
  if (logo) {
    return { name: BRAND.name, logo, logoSmall: logo, source: 'pharmacy' };
  }
  return { name: BRAND.name, logo: BRAND.logo, logoSmall: BRAND.logoSmall, source: 'build' };
}

export function usePharmacyBranding(): ResolvedBranding {
  const activePharmacy = usePharmacyStore((s) => s.activePharmacy);
  return useMemo(() => resolveBranding(activePharmacy), [activePharmacy]);
}
